import React, { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getProductImg, handleImgError } from "../../utils/imageHelper";
import "../../styles/invoice.css";

const InvoiceModal = ({ order, isOpen, onClose }) => {
  const invoiceRef = useRef(null);

  if (!isOpen || !order) return null;

  const handlePrint = () => {
    if (!invoiceRef.current) {
      window.print();
      return;
    }

    const printContent = invoiceRef.current.innerHTML;

    // Create an isolated hidden iframe for clean, full-content printing
    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Print Atelier Invoice");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>L'HABILLEUR Atelier Invoice #${order.orderNumber || "Invoice"}</title>
        <style>
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            color: #1e293b;
            background: #ffffff;
            padding: 24px 30px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: A4;
            margin: 12mm 15mm;
          }
          .invoice__header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding-bottom: 20px;
            border-bottom: 2px solid #0a1d37;
            margin-bottom: 24px;
          }
          .invoice__brand h2 {
            font-size: 1.6rem;
            font-weight: 800;
            letter-spacing: 2px;
            color: #0a1d37;
            margin: 0;
          }
          .invoice__brand span {
            font-size: 0.75rem;
            font-weight: 700;
            letter-spacing: 3px;
            color: #c98518;
            display: block;
            margin: 2px 0 6px 0;
          }
          .invoice__brand p {
            font-size: 0.8rem;
            color: #64748b;
            line-height: 1.45;
          }
          .invoice__meta {
            text-align: right;
          }
          .invoice__meta-title {
            font-size: 1.3rem;
            font-weight: 800;
            color: #0a1d37;
            margin: 0 0 4px 0;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .invoice__meta-item {
            font-size: 0.85rem;
            color: #475569;
            margin-bottom: 3px;
          }
          .invoice__meta-item strong {
            color: #0a1d37;
          }
          .text-success {
            color: #16a34a !important;
          }
          .text-uppercase {
            text-transform: uppercase;
          }
          .text-muted {
            color: #64748b !important;
          }
          .text-center {
            text-align: center;
          }
          .text-end {
            text-align: right;
          }
          .fw-bold {
            font-weight: 700;
          }
          .small {
            font-size: 0.78rem;
          }
          .invoice__info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 24px;
            gap: 24px;
          }
          .invoice__info-col {
            flex: 1;
          }
          .invoice__info-col h6 {
            font-size: 0.78rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #64748b;
            margin-bottom: 8px;
            border-bottom: 1px solid #e2e8f0;
            padding-bottom: 4px;
          }
          .invoice__info-col p {
            font-size: 0.86rem;
            color: #1e293b;
            margin: 0 0 3px 0;
            line-height: 1.4;
          }
          .invoice__table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 24px;
          }
          .invoice__table th {
            background: #f8fafc;
            color: #0a1d37;
            font-size: 0.8rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.8px;
            padding: 10px 14px;
            border-bottom: 1.5px solid #cbd5e1;
            text-align: left;
          }
          .invoice__table td {
            padding: 12px 14px;
            font-size: 0.86rem;
            border-bottom: 1px solid #f1f5f9;
            color: #334155;
            vertical-align: middle;
          }
          .invoice__item-info {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .invoice__item-thumb {
            width: 44px;
            height: 44px;
            object-fit: contain;
            background: #f8fafc;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
          }
          .invoice__summary-row {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 24px;
          }
          .invoice__summary-card {
            width: 280px;
          }
          .invoice__calc-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            font-size: 0.88rem;
            color: #475569;
          }
          .invoice__calc-row.total {
            border-top: 2px solid #0a1d37;
            margin-top: 6px;
            padding-top: 8px;
            font-size: 1.12rem;
            font-weight: 800;
            color: #0a1d37;
          }
          .invoice__footer {
            border-top: 1px solid #e2e8f0;
            padding-top: 18px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
          }
          .invoice__warranty {
            font-size: 0.76rem;
            color: #64748b;
            max-width: 460px;
            line-height: 1.45;
          }
          .invoice__stamp {
            border: 1.5px dashed #c98518;
            color: #c98518;
            padding: 6px 14px;
            border-radius: 8px;
            font-size: 0.72rem;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <div class="invoice__sheet">
          ${printContent}
        </div>
      </body>
      </html>
    `);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 2000);
    }, 250);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Recent Order";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <AnimatePresence>
      <div className="invoice__backdrop" onClick={onClose}>
        <motion.div
          className="invoice__modal"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2 }}
        >
          {/* Actions Bar (Not Printed) */}
          <div className="invoice__actions-bar">
            <span className="invoice__actions-bar-title">
              <i className="ri-file-text-line"></i> Atelier Official Invoice #
              {order.orderNumber}
            </span>

            <div className="invoice__actions-btns">
              <button
                type="button"
                className="invoice__print-btn"
                onClick={handlePrint}
                title="Print or Save as PDF"
              >
                <i className="ri-printer-line"></i> Print / Save PDF
              </button>

              <button
                type="button"
                className="invoice__close-btn"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>

          {/* Printable Official Sheet */}
          <div className="invoice__sheet" ref={invoiceRef}>
            {/* Header / Brand & Invoice Meta */}
            <div className="invoice__header">
              <div className="invoice__brand">
                <h2>L'HABILLEUR</h2>
                <span>ATELIER DE MAISON</span>
                <p>
                  Flagship Luxury Living Gallery
                  <br />
                  5th Settlement, New Cairo, Egypt
                  <br />
                  concierge@lhabilleur.com &bull; +20 100 000 0000
                </p>
              </div>

              <div className="invoice__meta">
                <h3 className="invoice__meta-title">Official Invoice</h3>
                <div className="invoice__meta-item">
                  Invoice No: <strong>#INV-{order.orderNumber}</strong>
                </div>
                <div className="invoice__meta-item">
                  Date: <strong>{formatDate(order.createdAt)}</strong>
                </div>
                <div className="invoice__meta-item">
                  Status:{" "}
                  <strong className="text-uppercase text-success">
                    {order.status || "Confirmed & Verified"}
                  </strong>
                </div>
              </div>
            </div>

            {/* Customer & Billing Information */}
            <div className="invoice__info-row">
              <div className="invoice__info-col">
                <h6>Billed & Shipped To</h6>
                <p>
                  <strong>{order.customerName}</strong>
                </p>
                <p>{order.shippingAddress?.address}</p>
                <p>
                  {order.shippingAddress?.city},{" "}
                  {order.shippingAddress?.country || "Egypt"}
                </p>
                <p>
                  <i className="ri-phone-line me-1"></i> {order.phoneNumber}
                </p>
                <p className="text-muted small">{order.email}</p>
              </div>

              <div className="invoice__info-col">
                <h6>Delivery & Payment Details</h6>
                <p>
                  <strong>Service:</strong> White-Glove In-Home Delivery
                </p>
                <p>
                  <strong>Payment Method:</strong> Cash on Delivery (COD)
                </p>
                <p>
                  <strong>Atelier Client ID:</strong> {order.userId?.slice(0, 10)}...
                </p>
              </div>
            </div>

            {/* Products Table */}
            <table className="invoice__table">
              <thead>
                <tr>
                  <th>Item & Specification</th>
                  <th className="text-center">Qty</th>
                  <th className="text-end">Unit Price</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <div className="invoice__item-info">
                        <img
                          src={getProductImg(item)}
                          onError={(e) => handleImgError(e, item)}
                          alt={item.productName}
                          className="invoice__item-thumb"
                        />
                        <div>
                          <strong>{item.productName}</strong>
                          <div className="text-muted small">
                            Atelier Heritage Furniture Collection
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-center">{item.quantity}</td>
                    <td className="text-end">${item.price}</td>
                    <td className="text-end fw-bold">
                      ${item.totalPrice || item.price * item.quantity}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Calculations Summary */}
            <div className="invoice__summary-row">
              <div className="invoice__summary-card">
                <div className="invoice__calc-row">
                  <span>Subtotal:</span>
                  <span>
                    ${(order.items || []).reduce((acc, i) => acc + (Number(i.price || 0) * Number(i.quantity || 1)), 0) || order.totalAmount}
                  </span>
                </div>
                {order.discountAmount > 0 && (
                  <div className="invoice__calc-row" style={{ color: '#b45309', fontWeight: 600 }}>
                    <span>Privilege Voucher ({order.couponCode || 'PROMO'}):</span>
                    <span>-${order.discountAmount}</span>
                  </div>
                )}
                <div className="invoice__calc-row">
                  <span>White-Glove Delivery:</span>
                  <span className="text-success fw-bold">FREE</span>
                </div>
                <div className="invoice__calc-row">
                  <span>VAT (14% Included):</span>
                  <span>$0.00</span>
                </div>
                <div className="invoice__calc-row total">
                  <span>Grand Total:</span>
                  <span>${order.totalAmount}</span>
                </div>
              </div>
            </div>

            {/* Footer Seal & Legal Warranty */}
            <div className="invoice__footer">
              <p className="invoice__warranty">
                <strong>Authenticity Guarantee:</strong> Each L'HABILLEUR piece is handcrafted with sustainable kiln-dried solid timber and artisanal textiles. Includes our official 5-Year Structural Frame Warranty and 30-Day Comfort Trial.
              </p>

              <div className="invoice__stamp">
                Certified Authentic
                <br />
                L'HABILLEUR ATELIER
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default InvoiceModal;
